import type { Ref } from 'vue'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'

export interface MeResponse {
  nni: string
  habilitation: string
  gardianProfiles?: string[]
  directionAdminDirLibs: string[]
  firstLoginAt: string | null
  lastLoginAt: string | null
  noticeLatestVersionId: number | null
  noticeAcceptedVersionId: number | null
  noticeAcceptedAt: string | null
  noticeUpToDate: boolean
}

/** Invalide les GET /v1/me encore en vol (plusieurs `useMe()` montent un watch chacun). */
let clientRefreshGeneration = 0
let clientRefreshInFlight: Promise<void> | null = null

export function useMe() {
  const { loggedIn, user } = useOidcAuth()
  // Partagé entre composants (NoticeGate, NoticeAcceptanceWall, layout, etc.)
  const me = useState<MeResponse | null>('monorga.me', () => null)

  watch(
    () => ({ isLoggedIn: loggedIn.value, accessToken: user.value?.accessToken }),
    (auth) => {
      void syncSharedMe(me, auth.isLoggedIn, auth.accessToken)
    },
    { immediate: true }
  )

  return {
    me,
    refreshMe: () => refreshSharedMe(me),
  }
}

async function syncSharedMe(
  me: Ref<MeResponse | null>,
  isLoggedIn: boolean,
  accessToken: string | undefined
): Promise<void> {
  if (import.meta.server) {
    return
  }
  if (!isLoggedIn || !accessToken) {
    clearSharedMe(me)
    return
  }
  await refreshSharedMe(me)
}

function clearSharedMe(me: Ref<MeResponse | null>): void {
  clientRefreshGeneration += 1
  clientRefreshInFlight = null
  me.value = null
}

async function refreshSharedMe(me: Ref<MeResponse | null>): Promise<void> {
  if (import.meta.server) {
    return
  }
  if (clientRefreshInFlight !== null) {
    await clientRefreshInFlight
    return
  }
  const generation = ++clientRefreshGeneration
  clientRefreshInFlight = storeFetchedMe(me, generation).finally(() => {
    clientRefreshInFlight = null
  })
  await clientRefreshInFlight
}

async function storeFetchedMe(me: Ref<MeResponse | null>, generation: number): Promise<void> {
  try {
    const next = await useApiMonOrga<MeResponse>('GET', '/v1/me')
    applyFetchedMe(me, next, generation)
  } catch (error) {
    if (generation !== clientRefreshGeneration) {
      return
    }
    console.error('Impossible de recuperer le profil utilisateur', error)
  }
}

function applyFetchedMe(me: Ref<MeResponse | null>, next: MeResponse, generation: number): void {
  if (generation !== clientRefreshGeneration) {
    return
  }
  const nextLibs = directionAdminLibs(next)
  if (shouldKeepPreviousDirectionAdminLibs(me.value, next.nni, nextLibs)) {
    return
  }
  me.value = {
    ...next,
    directionAdminDirLibs: nextLibs,
  }
}

function directionAdminLibs(next: MeResponse): string[] {
  if (Array.isArray(next.directionAdminDirLibs)) {
    return next.directionAdminDirLibs
  }
  return []
}

function shouldKeepPreviousDirectionAdminLibs(
  current: MeResponse | null,
  nextNni: string,
  nextLibs: string[]
): boolean {
  if (current === null || current.nni !== nextNni) {
    return false
  }
  return current.directionAdminDirLibs.length > 0 && nextLibs.length === 0
}
